import React, { useContext, useState, useCallback } from 'react';
import produce, { Draft } from 'immer';
import { useNavigate } from 'react-router-dom';
import { showDialog, DialogType, modeUIContext } from '@moderepo/mode-ui-state';
import { capitalizeAll, EMAIL_VALIDATION_REGEX, getErrorMessage, StartResetPasswordForm } from '@moderepo/mode-app-components';
import { ApiError } from '@moderepo/mode-apis';
import { DEFAULT_ORGANIZATION_ID, RitzAPI } from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { NotAuthorized, RouteHelper } from '..';
import { LanguageSelectorContainer } from '../language-selector/LanguageSelectorContainer';
import { getOrganization } from '../app/App';


interface InputErrors {
    email?: string | undefined;
    otherError?: string | undefined;
}


/**
 * Request for reset password page. This page is used for agents to request for their password to be reset when
 * they forgot their password.
 */
export const ForgotPassword: React.FC = () => {
    const navigate = useNavigate();
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { trans } = useRitzTranslation(undefined);
    const [inputErrors, setInputErrors] = useState<InputErrors>({ });
    const routeHelper = RouteHelper.getInstance(navigate);
    const organization = getOrganization();


    const onFormSubmit = useCallback(async (inputs: {
        readonly email: string | undefined
    }) => {

        if (!organization) {
            return false;
        }

        const { email } = inputs;
        const newInputErrors: InputErrors = { };

        if (email && email.length > 0) {
            if (!EMAIL_VALIDATION_REGEX.test(email)) {
                newInputErrors.email = 'error.input.email.invalid';
            }
        } else {
            newInputErrors.email = 'error.input.email.empty';
        }

        setInputErrors(newInputErrors);

        if (!newInputErrors.email && email) {
            // email passed validation, submit reset password now
            RitzAPI.getInstance().requestResetPassword(organization.id, email).then(() => {
                uiDispatch(showDialog({
                    type          : DialogType.SUCCESS,
                    title         : 'label.success',
                    message       : 'notif.password_reset.started',
                    positiveButton: 'label.ok',
                }));
            }).catch((error: ApiError) => {
                setInputErrors({
                    ...inputErrors,
                    otherError: getErrorMessage((error as ApiError).code),
                });
            });
        }

        return false;
    }, [inputErrors, organization, uiDispatch]);


    // If organization is undefined then that mean we weren't able to get organization based on baseUrl or orgId in the params
    if (!organization) {
        return <NotAuthorized />;
    }


    return (
        <StartResetPasswordForm
            texts={{
                title   : trans('forgot_password_form.title', { org_name: organization.name }),
                subtitle: organization.id === DEFAULT_ORGANIZATION_ID
                    ? trans('app.title_default')
                    : trans('app.title', { org_name: organization.name }),
                resetPasswordMessage    : trans('forgot_password_form.message'),
                emailInputLabel         : capitalizeAll(trans('label.email')),
                loginButtonLabel        : trans('label.login'),
                resetPasswordButtonLabel: trans('label.reset_password'),
            }}
            errorTexts={{
                email     : trans(inputErrors.email || ''),
                otherError: inputErrors.otherError ? trans(inputErrors.otherError) : '',
            }}
            languageSelectorComponent={<LanguageSelectorContainer projectId={undefined} minimal />}
            onGotoLogin={() => {
                routeHelper.gotoLoginPage();
            }}
            onFormInputChange={(field: string) => {
                setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                    draft[field] = undefined;
                    draft.otherError = undefined;
                }));
            }}
            onFormSubmit={onFormSubmit}
        />
    );

};
